import { useEffect, useRef, useState } from 'react';
import { getConvertedTotal } from '../api/endpointsBackend';
import { useDisplayCurrency } from '../contexts/DisplayCurrencyContext';

/**
 * MI-318 — converted values for the movement rows currently on screen.
 *
 * Asks only for the ids it can see (`include_totals=false`), so the cost is an
 * id lookup rather than a rescan of the filtered period. That matters in the
 * grouped view, where expanding a group adds rows one group at a time.
 *
 * Results accumulate across calls: a group you expanded stays converted when
 * you expand the next one, and only the newly-visible ids are ever requested.
 * The accumulated map is dropped whenever the display currency changes, since
 * every value in it is denominated in the old one.
 *
 * `resolved` is what makes "still loading" distinguishable from "could not be
 * converted": both are absent from `amounts`, so without it an unconvertible row
 * would sit in the pending state forever.
 *
 * @param {Array<number>} ids  movement ids being rendered
 * @param {Object} opts        { enabled }
 * `sources` maps an id to the rates its value read (MI-329, `utils/rateSources`).
 *
 * @returns {{ amounts: Object|null, sources: Object, resolved: Set<number>, displayCurrency: string|null, loading: boolean }}
 */
export function useConvertedRows(ids = [], { enabled = true } = {}) {
  const { displayCurrency } = useDisplayCurrency();
  const [amounts, setAmounts] = useState(null);
  const [sources, setSources] = useState({});
  // Every id the server has answered about — converted or not.
  const [resolved, setResolved] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  // What we already have (or have in flight), so ids are never asked for twice.
  const knownRef = useRef(new Set());
  const currencyRef = useRef(null);
  // Requests still open, so a currency switch can cancel all of them at once.
  const inFlightRef = useRef(new Set());

  // A stable key: the effect must re-run when the *set* changes, not when the
  // array identity does — the list rebuilds its arrays on every render.
  const idsKey = ids && ids.length ? ids.join(',') : '';

  useEffect(() => {
    if (currencyRef.current !== displayCurrency) {
      // Every cached value is in the previous currency — start over.
      currencyRef.current = displayCurrency;
      knownRef.current = new Set();
      setAmounts(null);
      setSources({});
      setResolved(new Set());
    }
    if (!displayCurrency || !enabled) return undefined;

    const wanted = (idsKey ? idsKey.split(',') : [])
      .map(Number)
      .filter((id) => Number.isFinite(id) && !knownRef.current.has(id));
    if (wanted.length === 0) return undefined;

    wanted.forEach((id) => knownRef.current.add(id));
    const controller = new AbortController();
    inFlightRef.current.add(controller);
    const epoch = displayCurrency;
    setLoading(true);
    getConvertedTotal(displayCurrency, [], null, null, [], 'and', {
      signal: controller.signal,
      movementIds: wanted,
      includeTotals: false,
    })
      .then((data) => {
        const next = {};
        const nextSources = {};
        (data?.movements || []).forEach((m) => {
          next[m.id] = m.converted_amount;
          nextSources[m.id] = m.rate_sources || [];
        });
        setAmounts((prev) => ({ ...(prev || {}), ...next }));
        setSources((prev) => ({ ...prev, ...nextSources }));
        // Every id asked for is now answered, including the ones that came back
        // with nothing — those are unconvertible, not pending.
        setResolved((prev) => {
          const merged = new Set(prev);
          wanted.forEach((id) => merged.add(id));
          return merged;
        });
        setLoading(false);
      })
      .catch(() => {
        // Let the ids be retried rather than leaving them permanently "known".
        // Cancellations from a currency switch are the one exception: those ids
        // belong to a map that has already been thrown away, and releasing them
        // would delete entries the new currency has just claimed.
        if (currencyRef.current === epoch) {
          wanted.forEach((id) => knownRef.current.delete(id));
        }
        setLoading(false);
      })
      .finally(() => { inFlightRef.current.delete(controller); });

    // Deliberately no abort on cleanup. Every batch is additive — it asks only
    // for ids no earlier batch covered — so cancelling the previous one on each
    // new id cannot help, and it silently strands rows: the ids were already in
    // `knownRef`, the cancelled request never answered for them, and no later
    // batch would ever ask again. That is the bug where some rows stayed in the
    // original currency, dimmed, for as long as the page was open, and why more
    // of them stranded the more groups were expanded. The requests are aborted
    // when the currency changes or the provider unmounts, below.
    return undefined;
  }, [displayCurrency, idsKey, enabled]);

  // The one place cancelling is right: every value in flight is denominated in
  // a currency nothing on screen is asking for any more.
  useEffect(() => () => {
    inFlightRef.current.forEach((c) => c.abort());
    inFlightRef.current.clear();
  }, [displayCurrency]);

  return { amounts, sources, resolved, displayCurrency, loading };
}
